/**
 * Parse and validate a single calendar day for student leave requests.
 * Stores use UTC midnight for that calendar day to avoid TZ drift across clients.
 */

const MIN_LEAVE_YEAR = 1950;
const MAX_YEARS_FUTURE = 5;

export type ParsedLeaveDay =
  | { ok: true; utcDayStart: Date }
  | { ok: false; message: string };

function buildUtcMidnight(year: number, month0: number, day: number): Date {
  return new Date(Date.UTC(year, month0, day));
}

function utcCalendarDay(date: Date): Date {
  return buildUtcMidnight(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  );
}

function applyRangeCheck(utcMidnight: Date): ParsedLeaveDay {
  const minAllowed = buildUtcMidnight(MIN_LEAVE_YEAR, 0, 1);
  const maxAllowed = buildUtcMidnight(
    new Date().getUTCFullYear() + MAX_YEARS_FUTURE,
    11,
    31,
  );
  if (utcMidnight < minAllowed) {
    return {
      ok: false,
      message: `Leave date cannot be before ${MIN_LEAVE_YEAR}-01-01.`,
    };
  }
  if (utcMidnight > maxAllowed) {
    return {
      ok: false,
      message: `Leave date cannot be more than ${MAX_YEARS_FUTURE} years in the future.`,
    };
  }
  return { ok: true, utcDayStart: utcMidnight };
}

/**
 * Validates YYYY-MM-DD with a real calendar day (no Feb 30, etc.).
 */
function parseStrictYyyyMmDd(s: string): ParsedLeaveDay {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) {
    return {
      ok: false,
      message:
        "Invalid date format. Use ISO 8601 calendar date YYYY-MM-DD (e.g. 2026-04-30).",
    };
  }
  const year = Number(m[1]);
  const month0 = Number(m[2]) - 1;
  const day = Number(m[3]);
  const utcMidnight = buildUtcMidnight(year, month0, day);
  if (
    utcMidnight.getUTCFullYear() !== year ||
    utcMidnight.getUTCMonth() !== month0 ||
    utcMidnight.getUTCDate() !== day
  ) {
    return {
      ok: false,
      message:
        "Invalid date: that day does not exist for the given month and year.",
    };
  }
  return applyRangeCheck(utcMidnight);
}

/**
 * Accept ISO 8601 datetimes by taking the UTC calendar date (e.g. from some clients).
 */
function parseIsoToUtcDay(s: string): ParsedLeaveDay {
  const parsed = Date.parse(s.trim());
  if (Number.isNaN(parsed)) {
    return {
      ok: false,
      message:
        "Invalid date format. Use ISO 8601 calendar date YYYY-MM-DD (e.g. 2026-04-30).",
    };
  }
  const utcMidnight = utcCalendarDay(new Date(parsed));
  return applyRangeCheck(utcMidnight);
}

/**
 * Parses `date` from the apply-for-leave API: prefers strict YYYY-MM-DD, falls back to ISO 8601.
 */
export function parseStudentLeaveDayInput(raw: unknown): ParsedLeaveDay {
  let s: string;

  if (raw === undefined || raw === null) {
    return { ok: false, message: "date is required." };
  }
  if (typeof raw === "string") {
    s = raw.trim();
    if (s === "") {
      return { ok: false, message: "date is required." };
    }
  } else if (typeof raw === "number" && Number.isFinite(raw)) {
    s = String(raw);
  } else if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) {
      return {
        ok: false,
        message:
          "Invalid date format. Use ISO 8601 calendar date YYYY-MM-DD (e.g. 2026-04-30).",
      };
    }
    s = raw.toISOString();
  } else {
    return {
      ok: false,
      message:
        "Invalid date format. Use ISO 8601 calendar date YYYY-MM-DD (e.g. 2026-04-30).",
    };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return parseStrictYyyyMmDd(s);
  }
  return parseIsoToUtcDay(s);
}

export function utcCalendarDayStart(date: Date): Date {
  return utcCalendarDay(date);
}
